export enum Role {
  Patient = "Patient",
  Provider = "Provider",
  Admin = "Administrator",
  Developer = "Developer",
  Nurse = "Nurse",
  MedicalAssistant = "Medical Assistant",
  AuthTeam = "Auth Team",
  PacTeam = "PAC Team",
  MtcDoc = "MTC Doc",
  ExternalProvider = "External Provider",
  // Delegated office staff acting under an external provider's org. Value MUST match the backend
  // RoleNames.ExternalOrgUser ("External Org Staff") — it's the wire string compared in role checks.
  ExternalOrgUser = "External Org Staff",
  // Family member / guardian with scoped access to a specific patient's portal. Value MUST match
  // the backend RoleNames.Caregiver ("Caregiver").
  Caregiver = "Caregiver",
}

/**
 * Who may see questionnaires — Sam, round-two Q3.3 (2026-09-11): Medical Assistants,
 * Administrators, MTC Docs and Providers; "nurses do not need to see questionnaires". Developer is
 * the engineering role and is kept. Mirrors the backend `RoleNames.QuestionnaireRoles`, which is what
 * actually refuses: this list only keeps the UI from offering what the server will 403.
 */
/**
 * Mirrors the backend RoleNames.AdminOrMedicalAssistantOrNurseOrProviderOrMtcDoc — the staff who may
 * read a patient's record, including their visit lists (/appointment-slots/patient/{id}/...).
 */
export const PATIENT_RECORD_STAFF_ROLES: Role[] = [
  Role.Admin,
  Role.MedicalAssistant,
  Role.Nurse,
  Role.Provider,
  Role.MtcDoc,
];

/**
 * Mirrors the backend RoleNames.MtcDocOrProviderOrNurseOrMedicalAssistant — who may create a patient
 * (POST /patients) and write to their record, including the infusion edits, pause/resume, new
 * infusion series and mark complete/not complete on PatientsController. Administrators read but do
 * not write these.
 */
export const PATIENT_EDITOR_ROLES: Role[] = [
  Role.MtcDoc,
  Role.Provider,
  Role.Nurse,
  Role.MedicalAssistant,
];

/** Mirrors the backend RoleNames.MriWorkflowRoles — mark an infusion's MRI ordered / ready (INF-11). */
export const MRI_WORKFLOW_ROLES: Role[] = [Role.MedicalAssistant, Role.Provider];

/**
 * Mirrors the backend RoleNames.AdminOrMedicalAssistantOrProviderOrMtcDoc — resend an infusion's MRI
 * approval request (POST /patients/{id}/infusions/{infusionId}/resend-approval-request).
 */
export const MRI_APPROVAL_RESEND_ROLES: Role[] = [
  Role.Admin,
  Role.MedicalAssistant,
  Role.Provider,
  Role.MtcDoc,
];

/**
 * Who can actually delete infusions (POST /patients/{id}/infusions/delete): the action says
 * RoleNames.AdminOrPac, but ASP.NET Core ANDs it with PatientsController's class gate
 * (AdminOrMedicalAssistantOrNurseOrProviderOrMtcDoc), so the effective set is Administrator alone.
 */
/**
 * Who may see the MRIs-needing-approval list. Mirrors MrisController's class gate,
 * RoleNames.AdminOrMedicalAssistantOrProviderOrMtcDoc; the dashboard card uses the same set.
 */
export const MRI_REVIEW_ROLES: Role[] = [Role.Admin, Role.MedicalAssistant, Role.Provider, Role.MtcDoc];

export const INFUSION_DELETE_ROLES: Role[] = [Role.Admin];

/** Mirrors the backend RoleNames.CalendarManagers — who may read /providers/{id}/timeslots. */
export const CALENDAR_MANAGER_ROLES: Role[] = [
  Role.Provider,
  Role.MtcDoc,
  Role.PacTeam,
  Role.MedicalAssistant,
  Role.Admin,
];

/**
 * Who may read GET /infusions: the backend's class-level InfusionsControllerRoles intersected with
 * that action's AdminOrMedicalAssistantOrNurseOrProviderOrMtcDoc. The endpoint is also behind the
 * `infusions` module, so check that too.
 */
export const INFUSION_SCHEDULE_ROLES: Role[] = [
  Role.Admin,
  Role.MedicalAssistant,
  Role.Nurse,
  Role.Provider,
  Role.MtcDoc,
];

export const QUESTIONNAIRE_ROLES: Role[] = [
  Role.Admin,
  Role.Developer,
  Role.MedicalAssistant,
  Role.Provider,
  Role.MtcDoc,
];

/**
 * Who may record that a patient needs a video visit (NEW-74). Mirrors the backend
 * `RoleNames.MtcDocOrProviderOrNurseOrMedicalAssistant`, which is what actually refuses — this list
 * only keeps the UI from offering what the server would 403.
 *
 * Deliberately NOT narrowed to the two prescribing roles: the server already accepts nurses and
 * medical assistants, and a client-side gate that hides a capability the API grants makes the
 * product look broken rather than making it safer. Whether intake staff should be the ones deciding
 * a visit is clinically required — which also lowers what the patient pays — is a product question
 * in the same family as Q3.3, not something to settle by quietly omitting the button.
 */
export const VISIT_REQUIREMENT_ROLES: Role[] = [
  Role.MtcDoc,
  Role.Provider,
  Role.Nurse,
  Role.MedicalAssistant,
];

/**
 * The Q3.3 set plus the external roles: referring providers and their office staff keep the
 * questionnaire surfaces they already had (the referral intake, and the latest submission at their
 * care level). Q3.3 narrows INTERNAL access — it is about nurses — not the external portal.
 */
export const QUESTIONNAIRE_ROLES_OR_EXTERNAL: Role[] = [
  ...QUESTIONNAIRE_ROLES,
  Role.ExternalProvider,
  Role.ExternalOrgUser,
];
